/**
 * Database sync status for the Smart CMS top bar.
 *
 * Renders NOTHING unless a write has failed.
 *
 * Changes are written to the database as they are made, so there is no
 * "saving" state worth surfacing -- a chip that blinks on every keystroke is
 * noise. The only thing a user can act on is a change that did not reach the
 * database, and that is worth being loud about.
 */

import { Button } from "@/components/ui/button"
import { AlertTriangle } from "lucide-react"
import type { SyncStatus } from "../persistence/syncBridge"

export interface SyncIndicatorProps {
  status: SyncStatus
  onFlush?: () => void
}

export function SyncIndicator({ status, onFlush }: SyncIndicatorProps) {
  const { state, error } = status

  /*
   * ONLY failures are shown.
   *
   * Writes land within a frame or two of the action, so a "saving" or
   * "unsaved" chip would flicker on every keystroke while telling the user
   * nothing they can act on -- saving is the expected outcome and needs no
   * announcement. A write that did NOT land is the only state worth
   * interrupting for.
   */
  if (state !== "error") return null

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 gap-1.5 px-2 text-xs text-destructive"
      title={`Could not save: ${error}. Your last change is not in the database. Click to retry.`}
      onClick={onFlush}
    >
      <AlertTriangle className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">Not saved</span>
    </Button>
  )
}
